import { useEffect, useState } from 'react';
import { Typography, Card, Space, Divider, List, Tag, message, Tooltip, Table } from 'antd';
import { InfoCircleOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ReportsService } from '../api/services/reports.service';
import { useAuth } from '../store/auth';
import LinkifyText from '../components/LinkifyText';

const { Title, Text, Paragraph } = Typography;

export default function ReportsPage() {
  const { user } = useAuth();
  
  const [todayReport, setTodayReport] = useState(null);
  const [pastReports, setPastReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserReports(user.id);
    }
  }, [user]);

  const fetchUserReports = async (userId) => {
    setReportsLoading(true);
    try {
      const [todayRes, pastRes] = await Promise.all([
        ReportsService.getTodayReport(userId),
        ReportsService.getReports(userId)
      ]);
      setTodayReport(todayRes.data);
      setPastReports(pastRes.data);
    } catch (e) {
      message.error('Ошибка загрузки отчетов');
    }
    setReportsLoading(false);
  };

  const statusColors = { draft: 'default', submitted: 'processing', approved: 'success' };
  const statusLabels = { draft: 'Черновик', submitted: 'Отправлен', approved: 'Утверждён' };

  const formatHours = (hours) => {
    if (!hours) return '0 мин';
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0) return `${m} мин`;
    if (m === 0) return `${h} ч`;
    return `${h} ч ${m} мин`;
  };

  const actionColumns = [
    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      width: 150,
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Затраченное время',
      dataIndex: 'time_minutes',
      key: 'time_minutes',
      width: 150,
      render: (minutes) => `${minutes} мин`,
    },
  ];

  const renderActionsTable = (actionsData, hoursWorked) => {
    if (!actionsData || actionsData.length === 0) {
      return <Text type="secondary">Нет данных</Text>;
    }

    const totalMinutes = actionsData.reduce((sum, item) => sum + item.time_minutes, 0);

    return (
      <Table
        columns={actionColumns}
        dataSource={actionsData.map((item, idx) => ({ ...item, key: idx }))}
        pagination={false}
        size="small"
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2} align="right">
                <Text strong>Итого</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <Text strong>{formatHours(hoursWorked)}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    );
  };

  if (reportsLoading) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}><Text>Загрузка...</Text></div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Мои отчёты</h1>
        <p className="page-subtitle">Ежедневные отчёты о проделанной работе</p>
      </div>

      <Card 
        style={{ marginBottom: 24, borderRadius: 16, border: '1px solid #c7d2fe' }}
        styles={{ body: { padding: 24 } }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 12, 
          marginBottom: 16,
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
          borderRadius: 12,
        }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}>
            <ClockCircleOutlined />
          </div>
          <div>
            <Text strong style={{ fontSize: 16 }}>Сегодня</Text>
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>В реальном времени</Text>
          </div>
        </div>

        {todayReport ? (
          <>
            {todayReport.actions_data && todayReport.actions_data.length > 0 ? (
              renderActionsTable(todayReport.actions_data, todayReport.hours_worked)
            ) : (
              <div style={{ padding: '0 4px', marginBottom: 16 }}>
                <LinkifyText text={todayReport.content} />
              </div>
            )}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: 12,
              marginTop: 16,
            }}>
              <Text strong>Отработано: </Text>
              <Tag 
                color="blue" 
                style={{ 
                  fontSize: 16, 
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                {formatHours(todayReport.hours_worked)}
              </Tag>
              <Tooltip title={
                <div style={{ padding: '4px' }}>
                  <b>Как рассчитывается рабочее время?</b>
                  <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: 0 }}>
                    <li>Каждое ваше действие создаёт <b>отрезок в 3 минуты</b>.</li>
                    <li>Отрезки "склеиваются" в непрерывную работу.</li>
                    <li><b>Совещания не плюсуется</b> в рабочее время.</li>
                    <li>Личные чаты не учитываются.</li>
                  </ul>
                </div>
              }>
                <InfoCircleOutlined style={{ color: '#6366f1', cursor: 'pointer', fontSize: 16 }} />
              </Tooltip>
            </div>
          </>
        ) : (
          <Text type="secondary">Нет данных за сегодня</Text>
        )}
      </Card>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 18, color: '#0f172a' }}>Прошлые отчёты</Text>
      </div>
      
      <List
        itemLayout="vertical"
        dataSource={pastReports}
        renderItem={item => (
          <List.Item key={item.id} style={{ border: 'none', padding: '0 0 16px' }}>
            <Card 
              size="small" 
              style={{ borderRadius: 16 }}
              styles={{ body: { padding: 20 } }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileTextOutlined style={{ color: '#6366f1' }} />
                    <span style={{ fontWeight: 600 }}>
                      {dayjs(item.report_date).format('DD.MM.YYYY')} — {item.title}
                    </span>
                  </div>
                  <Tag 
                    color={statusColors[item.status] || 'default'}
                    style={{ borderRadius: 6 }}
                  >
                    {statusLabels[item.status] || item.status}
                  </Tag>
                </div>
              }
            >
              {item.actions_data && item.actions_data.length > 0 ? (
                renderActionsTable(item.actions_data, item.hours_worked)
              ) : (
                <>
                  <LinkifyText style={{ maxHeight: '200px', overflow: 'auto' }} text={item.content} />
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text strong>Отработано: </Text>
                    <Text>{formatHours(item.hours_worked)}</Text>
                  </div>
                </>
              )}
            </Card>
          </List.Item>
        )}
        locale={{ emptyText: 'Нет прошлых отчетов' }}
      />
    </div>
  );
}
